'use client';

import { useEffect, useRef, useMemo } from 'react';
import { DemoBookingBlock as IDemoBookingBlock } from '../types';
import { SupportedLanguages } from '@/types/globals';

// Fallbacks
const DEFAULT_SRC_BY_LOCALE: Record<SupportedLanguages, string> = {
  en: 'https://meetings.hubspot.com/jbarsalou/quickfacts-demo?embed=true',
  fr: 'https://meetings.hubspot.com/jbarsalou/demo-comparaisons-quickfacts?embed=true',
};

export default function DemoBookingBlock({
  block,
  locale,
}: {
  block: IDemoBookingBlock;
  locale: SupportedLanguages;
}) {
  const meetingsRef = useRef<HTMLDivElement>(null);

  const srcByLocale = useMemo(
    () => ({
      en: block.en_hubspot_url?.trim() || DEFAULT_SRC_BY_LOCALE.en,
      fr: block.fr_hubspot_url?.trim() || DEFAULT_SRC_BY_LOCALE.fr,
    }),
    [block.en_hubspot_url, block.fr_hubspot_url],
  );

  useEffect(() => {
    const currentRef = meetingsRef.current;

    if (currentRef) {
      // Simple, reliable iframe approach
      currentRef.innerHTML = `
        <iframe 
          src="${srcByLocale[locale]}"
          width="100%" 
          height="700"
          loading="lazy"
        ></iframe>
      `;
    }

    return () => {
      // Cleanup
      if (currentRef) {
        currentRef.innerHTML = '';
      }
    };
  }, [locale, srcByLocale]);

  return (
    <section>
      <div className="mx-auto max-w-4xl">
        {/* Header content */}
        <div className="px-4 text-left">
          {block.pre_title && (
            <p
              className="font-source-serif-pro text-qf-purple text-left text-sm font-semibold uppercase"
              dangerouslySetInnerHTML={{ __html: block.pre_title }}
            />
          )}
          {block.title && (
            <h3
              className="font-source-serif-pro text-2xl font-semibold"
              dangerouslySetInnerHTML={{ __html: block.title }}
            />
          )}
          {block.description && (
            <p
              className="font-source-serif-pro text-graphite-700 text-lg"
              dangerouslySetInnerHTML={{ __html: block.description }}
            />
          )}
        </div>

        {/* HubSpot Meetings Container */}
        <div className="rounded-lg bg-white">
          <div
            ref={meetingsRef}
            id="hubspot-meetings-demo"
            className="min-h-96"
          />
        </div>
      </div>
    </section>
  );
}

// Extend window type for HubSpot
declare global {
  interface Window {
    hsMeetingsEmbed: {
      init: (config: {
        url: string;
        target: HTMLElement;
        width?: string;
        height?: string;
        css?: string;
        onReady?: () => void;
        onMeetingBooked?: () => void;
      }) => void;
    };
  }
}
